/**
 * The vocabulary, defined once.
 *
 * The server validates with these, and so does the browser. A requirement that
 * the form accepts is a requirement the API accepts, because there is only one
 * definition of what a requirement is. This is the reason the backend is
 * TypeScript rather than Java.
 */
import { z } from 'zod'

/* -------------------------------------------------------------------------
 * Who is who
 * ---------------------------------------------------------------------- */

/** A buyer asks; a company answers. Nobody is both. */
export const ORGANIZATION_KINDS = ['BUYER', 'COMPANY'] as const
export const organizationKindSchema = z.enum(ORGANIZATION_KINDS)
export type OrganizationKind = z.infer<typeof organizationKindSchema>

/**
 * ADMIN is the person who runs the platform for one client. There is exactly
 * one client per deployment, so ADMIN has no organisation: they see everything
 * in this database, and there is nothing else in it.
 */
export const USER_ROLES = ['ADMIN', 'BUYER', 'COMPANY'] as const
export const userRoleSchema = z.enum(USER_ROLES)
export type UserRole = z.infer<typeof userRoleSchema>

/**
 * Anyone can register; nobody takes part until the admin approves them. That is
 * what keeps sign-up self-service without letting strangers see requirements.
 */
export const ACCOUNT_STATUSES = ['PENDING', 'APPROVED', 'REJECTED', 'SUSPENDED'] as const
export const accountStatusSchema = z.enum(ACCOUNT_STATUSES)
export type AccountStatus = z.infer<typeof accountStatusSchema>

/* -------------------------------------------------------------------------
 * The requirement
 * ---------------------------------------------------------------------- */

/**
 * QUOTED and IN_PROGRESS sit between ENQUIRED and CLOSED: the admin has picked
 * one mill's answer and put a price to the buyer (QUOTED), and the buyer has
 * accepted it (IN_PROGRESS). Before QUOTED, a buyer's own screen only ever
 * shows DRAFT/SUBMITTED/ENQUIRED — see [[requirementQuoteSchema]] and the
 * comment on `EnquiriesService.quote`.
 *
 * REJECTED is the admin declining a SUBMITTED requirement before any company
 * is asked — deliberately not something the buyer edits afterwards. There is
 * no "ask for changes" loop: rejected is terminal, and the way forward is a
 * new requirement, which already exists as a flow. See
 * `RequirementsService.reject`.
 */
export const REQUIREMENT_STATUSES = [
  'DRAFT',
  'SUBMITTED',
  'REJECTED',
  'ENQUIRED',
  'QUOTED',
  'IN_PROGRESS',
  'CLOSED',
] as const
export const requirementStatusSchema = z.enum(REQUIREMENT_STATUSES)
export type RequirementStatus = z.infer<typeof requirementStatusSchema>

/**
 * Knitted fabric is measured two ways and the industry mixes them freely:
 * tubular fabric is quoted in inches, open-width in centimetres. Storing the
 * number without the unit is how a 30 becomes indistinguishable from a 30 that
 * means something else entirely, so the unit travels with the value.
 */
export const WIDTH_UNITS = ['INCH', 'CM'] as const
export const widthUnitSchema = z.enum(WIDTH_UNITS)
export type WidthUnit = z.infer<typeof widthUnitSchema>

/** A percentage that may be stated to one decimal, as the mills write it. */
const percent = z.coerce.number().min(-100).max(100)

/**
 * The six measurements from the client's Buyer Requirement sheet, plus the
 * three things the sheet assumes you already know: what the fabric is, how much
 * is wanted, and by when.
 *
 * Everything but the fabric description is optional. A buyer often knows the
 * GSM and not the shrinkage, and refusing the enquiry until every box is filled
 * would send him back to WhatsApp, which is what this replaces.
 */
export const requirementInputSchema = z.object({
  /** Free text, because this is how a buyer actually asks: "30s combed cotton single jersey". */
  fabricDescription: z.string().trim().min(3).max(500),

  /** Set when the buyer picked this from the fabric catalogue rather than typing it from scratch. */
  fabricMasterId: z.uuid().nullish(),

  widthValue: z.coerce.number().positive().max(500).nullish(),
  widthUnit: widthUnitSchema.nullish(),
  gsm: z.coerce.number().int().positive().max(2000).nullish(),
  shrinkageLength: percent.nullish(),
  shrinkageWidth: percent.nullish(),
  spirality: percent.nullish(),
  /** The sheet asks "Repeat Required" as a question, so it is answered as one. */
  repeatRequired: z.boolean().nullish(),

  quantity: z.coerce.number().positive().max(1_000_000).nullish(),
  quantityUnit: z.string().trim().max(16).nullish(),
  requiredBy: z.iso.date().nullish(),
  notes: z.string().trim().max(2000).nullish(),
})
export type RequirementInput = z.infer<typeof requirementInputSchema>

/** A width value and a width unit are only meaningful together. */
export const requirementSchema = requirementInputSchema.refine(
  (value) => (value.widthValue == null) === (value.widthUnit == null),
  { message: 'State the width with its unit, or leave both blank', path: ['widthUnit'] },
)

/**
 * Editing a requirement. Every field optional, because a buyer correcting the
 * GSM should not have to resend the description.
 *
 * The width rule cannot be checked here: a patch carrying only `widthValue` is
 * legitimate when the stored row already has a unit. The server merges the
 * patch onto the row and validates the *result* with `requirementSchema`,
 * which is the only place both halves are known.
 */
export const requirementPatchSchema = requirementInputSchema.partial()
export type RequirementPatch = z.infer<typeof requirementPatchSchema>

/** Paging and filtering a list of requirements. */
export const requirementListQuerySchema = z.object({
  status: requirementStatusSchema.optional(),
  // Admin-only in effect: a buyer's own scope already restricts the list to
  // their own organisation, so this only narrows anything for the admin,
  // viewing one buyer's history from that organisation's own screen.
  buyerId: z.uuid().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).default(0),
})
export type RequirementListQuery = z.infer<typeof requirementListQuerySchema>

/* -------------------------------------------------------------------------
 * The enquiry and the reply
 * ---------------------------------------------------------------------- */

/**
 * The admin picking who to ask. Companies are named explicitly — there is no
 * matcher here and deliberately so; see the README. A list rather than one id
 * because asking six mills is one decision, not six.
 */
export const enquiryCreateSchema = z.object({
  companyIds: z.array(z.uuid()).min(1).max(50),
  dueAt: z.iso.date().nullish(),
  adminNote: z.string().trim().max(2000).nullish(),
})
export type EnquiryCreate = z.infer<typeof enquiryCreateSchema>

/**
 * The three answers the client named. IN_STOCK is its own answer rather than a
 * yes with a note, because it is the one that ends the conversation fastest:
 * the fabric exists and can ship.
 */
export const REPLY_ANSWERS = ['YES', 'NO', 'IN_STOCK'] as const
export const replyAnswerSchema = z.enum(REPLY_ANSWERS)
export type ReplyAnswer = z.infer<typeof replyAnswerSchema>

/** Why not. A bare no tells the admin nothing about whether to ask again. */
export const REPLY_REASONS = [
  'MACHINE_UNAVAILABLE',
  'RAW_MATERIAL_UNAVAILABLE',
  'TECHNICALLY_NOT_FEASIBLE',
  'QUANTITY_TOO_SMALL',
  'TIMELINE_TOO_SHORT',
  'OTHER',
] as const
export const replyReasonSchema = z.enum(REPLY_REASONS)
export type ReplyReason = z.infer<typeof replyReasonSchema>

const replyBaseSchema = z.object({
  answer: replyAnswerSchema,
  /** Per unit of quantity, in `currency`. */
  price: z.coerce.number().nonnegative().max(10_000_000).nullish(),
  currency: z.string().trim().length(3).toUpperCase().nullish(),
  leadTimeDays: z.coerce.number().int().nonnegative().max(365).nullish(),
  minQuantity: z.coerce.number().positive().max(1_000_000).nullish(),
  reason: replyReasonSchema.nullish(),
  comments: z.string().trim().max(2000).nullish(),
})

/**
 * A yes has to be actionable and a no has to be explicable, so each one
 * requires the field that makes it useful. This is the whole product: the admin
 * is comparing prices and dates, and a yes without either is not an answer.
 */
export const replyInputSchema = replyBaseSchema.superRefine((value, context) => {
  if (value.answer !== 'NO') {
    if (value.price == null) {
      context.addIssue({ code: 'custom', path: ['price'], message: 'Give a price' })
    }
    if (value.currency == null) {
      context.addIssue({ code: 'custom', path: ['currency'], message: 'Give a currency' })
    }
  }
  if (value.answer === 'YES' && value.leadTimeDays == null) {
    context.addIssue({
      code: 'custom',
      path: ['leadTimeDays'],
      message: 'Say how many days it will take',
    })
  }
  if (value.answer === 'NO' && value.reason == null) {
    context.addIssue({ code: 'custom', path: ['reason'], message: 'Say why not' })
  }
})
export type ReplyInput = z.infer<typeof replyBaseSchema>

/* -------------------------------------------------------------------------
 * Attachments
 *
 * Not a Zod schema -- there is no JSON body to validate, the file itself is
 * the multipart payload. These two constants exist purely so the client's
 * `accept`/size hint and the server's real enforcement can't drift apart.
 * ---------------------------------------------------------------------- */

export const ATTACHMENT_CONTENT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'application/pdf',
] as const
export type AttachmentContentType = (typeof ATTACHMENT_CONTENT_TYPES)[number]

export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024

/* -------------------------------------------------------------------------
 * The fabric catalogue -- the admin's own edits
 *
 * Search and read need no schema (`GET /fabrics` takes a plain `q`). These
 * are the admin's write surface: adding a style by hand, editing one, or
 * deleting/restoring one. Only the fields that actually drive search,
 * display, and a requirement's prefill (`api/src/fabrics/service.ts`) are
 * here -- the catalogue carries ~30 more mill-internal columns (machine
 * code, dye code, route ref, ...) that today only a file import sets.
 * Nothing here stops a later field being added to this schema the same way
 * once the admin needs to hand-edit one of those too.
 * ---------------------------------------------------------------------- */

export const fabricCreateSchema = z.object({
  fabricCode: z.string().trim().min(1).max(32),
  structure: z.string().trim().max(64).nullish(),
  fibre: z.string().trim().max(32).nullish(),
  yarnCountRaw: z.string().trim().max(200).nullish(),
  yarnQuality: z.string().trim().max(32).nullish(),
  dyeMethod: z.string().trim().max(32).nullish(),
  construction: z.string().trim().max(32).nullish(),
  bedType: z.string().trim().max(32).nullish(),
  finishedGsm: z.coerce.number().int().positive().max(2000).nullish(),
  finishedWidth: z.coerce.number().positive().max(500).nullish(),
  widthUnit: widthUnitSchema.nullish(),
  stitchLengthMm: z.coerce.number().positive().max(50).nullish(),
})
export type FabricCreate = z.infer<typeof fabricCreateSchema>

/** Editing a style. Every field optional -- fixing the GSM shouldn't require retyping the code. */
export const fabricUpdateSchema = fabricCreateSchema.partial()
export type FabricUpdate = z.infer<typeof fabricUpdateSchema>

/** The mass upload -- an `.xlsx` with `docs/fabrics.csv`'s own ~40 columns, so it can run wide. */
export const MAX_FABRIC_IMPORT_BYTES = 20 * 1024 * 1024

/* -------------------------------------------------------------------------
 * The quote and the order
 * ---------------------------------------------------------------------- */

/**
 * The admin picking a winner and putting a number to the buyer.
 *
 * `replyId` names which mill's answer this is built from, but that identity
 * stops here: the buyer is shown `price`/`leadTimeDays`/`note`, never which
 * company they came from or which company's reply this was. The admin can
 * quote a different price than the mill actually gave — the client is a
 * broker, not a pass-through.
 */
export const requirementQuoteSchema = z.object({
  replyId: z.uuid(),
  price: z.coerce.number().nonnegative().max(10_000_000),
  currency: z.string().trim().length(3).toUpperCase(),
  leadTimeDays: z.coerce.number().int().nonnegative().max(365).nullish(),
  note: z.string().trim().max(2000).nullish(),
})
export type RequirementQuote = z.infer<typeof requirementQuoteSchema>

/**
 * The admin declining a SUBMITTED requirement, before any company sees it.
 *
 * The note is for the buyer this time — unlike `organizationDecisionSchema`'s
 * note, which is the admin's own memory and never leaves the server. Nothing
 * to fill in here means nothing to say beyond "not this one"; the buyer's own
 * next step is a new requirement either way.
 */
export const requirementRejectionSchema = z.object({
  note: z.string().trim().max(2000).nullish(),
})
export type RequirementRejection = z.infer<typeof requirementRejectionSchema>

/* -------------------------------------------------------------------------
 * Accounts
 * ---------------------------------------------------------------------- */

export const emailSchema = z.email().trim().toLowerCase().max(320)

/**
 * Long rather than complex. Composition rules push people towards `Passw0rd!`
 * and a mill owner types this on a phone.
 */
export const passwordSchema = z.string().min(12).max(200)

export const registrationSchema = z.object({
  organizationName: z.string().trim().min(2).max(200),
  organizationKind: organizationKindSchema,
  contactName: z.string().trim().min(2).max(200),
  email: emailSchema,
  phone: z.string().trim().min(6).max(32).nullish(),
  password: passwordSchema,
})
export type Registration = z.infer<typeof registrationSchema>

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1),
})
export type Login = z.infer<typeof loginSchema>

/**
 * Asking for a reset link. The response never says whether the address is
 * known: a form that answers "no such account" is an account-enumeration
 * oracle, and this one is reachable without signing in.
 */
export const passwordResetRequestSchema = z.object({
  email: emailSchema,
})
export type PasswordResetRequest = z.infer<typeof passwordResetRequestSchema>

/** Spending the link. The token is opaque and single-use. */
export const passwordResetSchema = z.object({
  token: z.string().min(20).max(200),
  password: passwordSchema,
})
export type PasswordReset = z.infer<typeof passwordResetSchema>

/**
 * Changing it while signed in. The current password is required even though
 * the session already proves identity — it is what stops a borrowed unlocked
 * phone from becoming a permanent takeover.
 */
export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1),
    newPassword: passwordSchema,
  })
  .refine((value) => value.currentPassword !== value.newPassword, {
    message: 'The new password must be different',
    path: ['newPassword'],
  })
export type ChangePassword = z.infer<typeof changePasswordSchema>

/* -------------------------------------------------------------------------
 * The admin's queue
 * ---------------------------------------------------------------------- */

/**
 * Approving, rejecting or suspending an organisation.
 *
 * The note is for the admin's own memory — "met at the Tirupur fair", "wrong
 * GST number, asked them to re-send". It is not shown to the organisation,
 * because a rejection reason written for oneself is rarely one you would send.
 */
export const organizationDecisionSchema = z.object({
  note: z.string().trim().max(2000).nullish(),
})
export type OrganizationDecision = z.infer<typeof organizationDecisionSchema>

/**
 * The admin adding a company or buyer directly, rather than waiting for it to
 * register itself — for the mills he already knows and is onboarding himself.
 * Reuses `registrationSchema`'s fields; it is the same information, just typed
 * in by the admin instead of by the organisation.
 */
export const organizationCreateSchema = z.object({
  kind: organizationKindSchema,
  name: z.string().trim().min(2).max(200),
  contactName: z.string().trim().min(2).max(200),
  email: emailSchema,
  phone: z.string().trim().min(6).max(32).nullish(),
  city: z.string().trim().min(1).max(120).nullish(),
})
export type OrganizationCreate = z.infer<typeof organizationCreateSchema>

/**
 * The admin adding a second person to a mill.
 *
 * No password: the admin does not choose one on someone else's behalf. The
 * account is created unusable and the person sets their own through the same
 * reset flow they would use if they had forgotten it.
 */
export const organizationUserCreateSchema = z.object({
  email: emailSchema,
  displayName: z.string().trim().min(2).max(200),
})
export type OrganizationUserCreate = z.infer<typeof organizationUserCreateSchema>

/**
 * Correcting an organisation's own details after the fact — a mistyped name,
 * a changed phone number. Not `kind`: that decides BUYER vs COMPANY
 * throughout, including every person's role inside it, and changing it after
 * people already exist would leave their roles describing an organisation
 * that no longer matches.
 */
export const organizationUpdateSchema = z.object({
  name: z.string().trim().min(2).max(200),
  contactName: z.string().trim().min(2).max(200),
  phone: z.string().trim().min(6).max(32).nullish(),
  city: z.string().trim().min(1).max(120).nullish(),
})
export type OrganizationUpdate = z.infer<typeof organizationUpdateSchema>

/**
 * Correcting a person's own details — most often a mistyped email caught
 * only once they try to sign in with it. Role still follows the
 * organisation and is not editable here, same as at creation.
 */
export const organizationUserUpdateSchema = z.object({
  email: emailSchema,
  displayName: z.string().trim().min(2).max(200),
})
export type OrganizationUserUpdate = z.infer<typeof organizationUserUpdateSchema>

export const organizationListQuerySchema = z.object({
  kind: organizationKindSchema.optional(),
  status: accountStatusSchema.optional(),
})
export type OrganizationListQuery = z.infer<typeof organizationListQuerySchema>

/* -------------------------------------------------------------------------
 * Notifications
 * ---------------------------------------------------------------------- */

/** Paging a list of notifications, and optionally only the unread ones. */
export const notificationListQuerySchema = z.object({
  unreadOnly: z.coerce.boolean().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).default(0),
})
export type NotificationListQuery = z.infer<typeof notificationListQuerySchema>

/* -------------------------------------------------------------------------
 * Push subscriptions
 * ---------------------------------------------------------------------- */

/**
 * What `PushSubscription.toJSON()` gives the browser, passed straight
 * through. One row per device, not per person — the same mill on two phones
 * holds two of these, and both hear about the same notification.
 */
export const pushSubscribeSchema = z.object({
  endpoint: z.string().url(),
  keys: z.object({
    p256dh: z.string().min(1),
    auth: z.string().min(1),
  }),
})
export type PushSubscribe = z.infer<typeof pushSubscribeSchema>

export const pushUnsubscribeSchema = z.object({
  endpoint: z.string().url(),
})
export type PushUnsubscribe = z.infer<typeof pushUnsubscribeSchema>

/* -------------------------------------------------------------------------
 * What a signed-in client knows about itself
 * ---------------------------------------------------------------------- */

/**
 * The shape `/auth/me` returns and every screen decides its layout from. The
 * organisation is null for exactly one person — the admin, who runs the
 * deployment and belongs to no company.
 */
export const authenticatedUserSchema = z.object({
  id: z.uuid(),
  email: z.string(),
  displayName: z.string(),
  role: userRoleSchema,
  status: accountStatusSchema,
  organization: z
    .object({
      id: z.uuid(),
      name: z.string(),
      kind: organizationKindSchema,
      status: accountStatusSchema,
    })
    .nullable(),
})
export type AuthenticatedUser = z.infer<typeof authenticatedUserSchema>

/**
 * A sign-in hands back a short-lived access token in the body and a refresh
 * token in an httpOnly cookie. Only the access token is ever visible to
 * JavaScript, so a scripting flaw steals fifteen minutes rather than a month.
 */
export const authSessionSchema = z.object({
  accessToken: z.string(),
  expiresInSeconds: z.number().int().positive(),
  user: authenticatedUserSchema,
})
export type AuthSession = z.infer<typeof authSessionSchema>
